import { useMemo, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { NAV, type NavSection } from './nav'
import { IconChevron, IconLogout } from '@/components/Icon'
import { cn } from '@/lib/cn'
import { podeAcessar } from '@/lib/permissoes'
import { useSessao } from '@/lib/SessaoProvider'

/**
 * Sidebar clara.
 *
 * Decisões e o que consertam:
 *  - fundo BRANCO com item ativo em azul (antes: gradiente slate-900→slate-800);
 *  - submenu em ACORDEÃO, não flyout no hover. O flyout antigo calculava posição
 *    com `getBoundingClientRect` e tinha bug ao recolher; acordeão funciona no
 *    mobile e não depende de posicionamento;
 *  - modo recolhido esconde o rótulo de verdade, com `title` para tooltip
 *    (antes: `font-size: 0`, que quebrava o layout — bug do briefing);
 *  - nada de `setInterval` reinjetando link;
 *  - **só aparecem as telas que o usuário pode acessar** (permissões por tela).
 *    Módulo que fica sem nenhuma tela liberada desaparece inteiro, em vez de
 *    abrir um submenu vazio.
 */

interface Props {
  isAdmin: boolean
  siscomexConectado: boolean
  /** 'production' | 'validation' — ambiente autenticado no Portal Único. */
  ambiente?: string | null
  usuario: { nome: string; email: string } | null
  onLogout: () => void
  /** Mobile: fecha o drawer ao navegar. */
  onNavigate?: () => void
}

export function Sidebar({
  isAdmin,
  siscomexConectado,
  ambiente,
  usuario,
  onLogout,
  onNavigate,
}: Props) {
  const { pathname } = useLocation()
  const [colapsada, setColapsada] = useState(false)

  // Começa com a seção da rota atual aberta.
  const secaoAtiva = NAV.find(
    (s) => s.children && (pathname === s.to || pathname.startsWith(`${s.to}/`)),
  )
  const [abertas, setAbertas] = useState<Set<string>>(
    () => new Set(secaoAtiva ? [secaoAtiva.label] : []),
  )

  const alternar = (label: string) =>
    setAbertas((prev) => {
      const s = new Set(prev)
      if (s.has(label)) s.delete(label)
      else s.add(label)
      return s
    })

  const sessao = useSessao()

  /**
   * Filtra a navegação pelas permissões.
   *
   * Uma seção com filhos só aparece se ao menos um filho estiver liberado, e o
   * submenu mostra apenas os liberados — assim não existe menu que leva a
   * "Sem permissão".
   */
  const itens = useMemo(() => {
    return NAV.filter((s) => !s.adminOnly || isAdmin)
      .map((s) => {
        if (!s.children?.length) return s
        const filhos = s.children.filter((f) => podeAcessar(sessao, f.to))
        return { ...s, children: filhos }
      })
      .filter((s) =>
        s.children?.length ? s.children.length > 0 : podeAcessar(sessao, s.to),
      )
  }, [isAdmin, sessao])

  return (
    <aside
      className={cn(
        'flex h-full flex-col border-r border-ink-200 bg-white',
        colapsada ? 'w-16' : 'w-60',
      )}
    >
      {/* ---------------------------------------------------------- marca */}
      <div className="flex h-14 shrink-0 items-center gap-2 border-b border-ink-200 px-3">
        <img
          src="/logo.png"
          alt="AutoComex"
          className={cn('object-contain', colapsada ? 'mx-auto h-7 w-7' : 'h-8 w-auto')}
        />
        {!colapsada && (
          <button
            type="button"
            onClick={() => setColapsada(true)}
            title="Recolher menu"
            aria-label="Recolher menu"
            className="ml-auto rounded p-1 text-ink-400 hover:bg-ink-100 hover:text-ink-600"
          >
            <IconChevron className="size-4 rotate-180" />
          </button>
        )}
      </div>

      {colapsada && (
        <button
          type="button"
          onClick={() => setColapsada(false)}
          title="Expandir menu"
          aria-label="Expandir menu"
          className="mx-auto mt-2 rounded p-1.5 text-ink-400 hover:bg-ink-100 hover:text-ink-600"
        >
          <IconChevron className="size-4" />
        </button>
      )}

      {/* ------------------------------------------------------- navegação */}
      <nav className="flex-1 overflow-y-auto px-2 py-3" aria-label="Menu principal">
        <ul className="space-y-0.5">
          {itens.map((secao) => (
            <SectionItem
              key={secao.label}
              secao={secao}
              colapsada={colapsada}
              aberta={abertas.has(secao.label)}
              onToggle={() => alternar(secao.label)}
              onNavigate={onNavigate}
            />
          ))}
        </ul>
      </nav>

      {/* ------------------------------------------------------ rodapé */}
      <div className="shrink-0 border-t border-ink-200 p-2">
        {/* Status do SISCOMEX: informação permanente, o despachante precisa saber. */}
        <div
          className={cn(
            'mb-2 flex items-center gap-2 rounded-md px-2 py-1.5 text-2xs font-medium',
            siscomexConectado
              ? 'bg-success-50 text-success-700'
              : 'bg-warning-50 text-warning-700',
            colapsada && 'justify-center',
          )}
          title={siscomexConectado ? 'SISCOMEX conectado' : 'SISCOMEX desconectado'}
        >
          <span
            className={cn(
              'size-1.5 shrink-0 rounded-full',
              siscomexConectado ? 'bg-success-600' : 'bg-warning-600',
            )}
          />
          {!colapsada && (siscomexConectado ? 'SISCOMEX conectado' : 'SISCOMEX desconectado')}
        </div>

        {/* Ambiente ativo (val ↔ prod). Validação ganha destaque para evitar
            registrar de verdade achando que está em teste. */}
        {ambiente && (
          <div
            className={cn(
              'mb-2 flex items-center gap-2 rounded-md px-2 py-1.5 text-2xs font-medium',
              ambiente === 'validation'
                ? 'bg-warning-50 text-warning-700'
                : 'bg-brand-50 text-brand-700',
              colapsada && 'justify-center',
            )}
            title={
              ambiente === 'validation'
                ? 'Ambiente de VALIDAÇÃO (homologação) — registros sem efeito real'
                : 'Ambiente de PRODUÇÃO — registros valem de verdade'
            }
          >
            <span
              className={cn(
                'size-1.5 shrink-0 rounded-full',
                ambiente === 'validation' ? 'bg-warning-600' : 'bg-brand-600',
              )}
            />
            {!colapsada && (ambiente === 'validation' ? 'Validação' : 'Produção')}
          </div>
        )}

        <div className={cn('flex items-center gap-2', colapsada && 'justify-center')}>
          <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-brand-100 text-2xs font-semibold text-brand-700">
            {(usuario?.nome ?? '?').charAt(0).toUpperCase()}
          </div>
          {!colapsada && (
            <>
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-medium text-ink-700">
                  {usuario?.nome ?? '—'}
                </p>
                <p className="truncate text-2xs text-ink-500">{usuario?.email ?? ''}</p>
              </div>
              <button
                type="button"
                onClick={onLogout}
                title="Sair"
                aria-label="Sair"
                className="rounded p-1 text-ink-400 hover:bg-ink-100 hover:text-danger-600"
              >
                <IconLogout />
              </button>
            </>
          )}
        </div>
      </div>
    </aside>
  )
}

/* -------------------------------------------------------------------------- */

function SectionItem({
  secao,
  colapsada,
  aberta,
  onToggle,
  onNavigate,
}: {
  secao: NavSection
  colapsada: boolean
  aberta: boolean
  onToggle: () => void
  onNavigate?: () => void
}) {
  const { pathname } = useLocation()
  const Icone = secao.icon
  const temFilhos = Boolean(secao.children?.length)
  const ativa = pathname === secao.to || pathname.startsWith(`${secao.to}/`)

  const base =
    'group flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-sm transition-colors'
  const estado = ativa
    ? 'bg-brand-50 font-medium text-brand-700'
    : 'text-ink-600 hover:bg-ink-100 hover:text-ink-800'

  return (
    <li>
      <div className="relative">
        {/* Barra azul de item ativo — mais sóbrio que pintar o fundo inteiro. */}
        {ativa && (
          <span className="absolute top-1 bottom-1 -left-2 w-0.5 rounded-r bg-brand-600" aria-hidden />
        )}

        {temFilhos && !colapsada ? (
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={aberta}
            className={cn(base, estado)}
          >
            <Icone className={cn('size-4 shrink-0', ativa ? 'text-brand-600' : 'text-ink-400')} />
            <span className="flex-1 text-left">{secao.label}</span>
            <IconChevron
              className={cn(
                'size-3.5 shrink-0 text-ink-400 transition-transform',
                aberta && 'rotate-90',
              )}
            />
          </button>
        ) : (
          <NavLink
            to={secao.to}
            onClick={onNavigate}
            title={colapsada ? secao.label : undefined}
            className={cn(base, estado, colapsada && 'justify-center px-0')}
          >
            <Icone className={cn('size-4 shrink-0', ativa ? 'text-brand-600' : 'text-ink-400')} />
            {!colapsada && <span className="flex-1">{secao.label}</span>}
          </NavLink>
        )}
      </div>

      {temFilhos && aberta && !colapsada && (
        <ul className="mt-0.5 mb-1 ml-4 space-y-0.5 border-l border-ink-200 pl-2">
          {secao.children!.map((filho) => (
            <li key={`${filho.group ?? ''}${filho.to}${filho.label}`}>
              {filho.group && (
                <p className="mt-2 mb-1 px-2 text-2xs font-semibold tracking-wide text-ink-400 uppercase">
                  {filho.group}
                </p>
              )}
              <NavLink
                to={filho.to}
                onClick={onNavigate}
                end
                className={({ isActive }) =>
                  cn(
                    'block rounded-md px-2 py-1 text-xs transition-colors',
                    isActive
                      ? 'bg-brand-50 font-medium text-brand-700'
                      : 'text-ink-600 hover:bg-ink-100 hover:text-ink-800',
                  )
                }
              >
                {filho.label}
              </NavLink>
            </li>
          ))}
        </ul>
      )}
    </li>
  )
}
